// Financial Reporting tab — Report Requests.
//
// B3 issues a request here, the borrower fulfils it as ONE bundled request in
// their existing portal Requests inbox, and B3 reviews it entirely on this tab.
// Replaces the retired catalog-driven receipt ledger, whose reads generated
// obligations: nothing on this surface creates anything. A request exists only
// because somebody issued it.
//
// THE SHAPE. Attention strip → type filter → that type's header → its requests.
// The strip says what needs eyes across every type; the filter narrows to one
// type; the header says what is owed next for it and whether it can be asked
// for; the list is the requests themselves, each opening its own lifecycle
// drawer.
//
// 🔴 THE FILTER IS A SEGMENTED CONTROL, NOT A SUB-TAB BAR. Layout Standard §6.5
// forbids a second tab strip inside a tab body — two rows of tabs make the page
// ask which one the reader is in — and prescribes filter chips, a segmented
// control, or a left rail. `FilterChips` is `role="group"` with `aria-pressed`,
// so it is a segmented control and reads as one to a screen reader too.
//
// 🔴 ALL FOUR CHIPS ALWAYS RENDER, INCLUDING AT ZERO. A measured zero is
// information — "no Annual request is open on this loan" is a fact somebody can
// act on — and it is not the same statement as a missing chip, which reads as
// "not loaded" or "not applicable here". This is the opposite call from the
// attention strip below it, which hides its zeros, and deliberately so: that
// strip's job is "what needs eyes", where a zero says nothing needs them.

import { useEffect, useMemo, useState } from "react";
import { Button, Card, FilterChips, Skeleton } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import type { LoanSummaryDto } from "@/api/portfolioAdmin/types";
import {
  REPORT_REQUEST_TYPE_LABEL,
  type ReportRequestType,
} from "@/api/portfolioAdmin/reportRequestsApi";
import { useLoanReportRequests } from "@/hooks/portfolioAdmin/useReportRequests";
import { useLoanHealth } from "@/hooks/portfolioAdmin/useLoanHealth";
import { AttentionStrip, type AttentionChip } from "../common/AttentionStrip";
import { DscrWatchPanel, DSCR_WATCH_TOGGLE_ID } from "./DscrWatchPanel";
import { TaxCompletionPanel, TAX_COMPLETION_INPUT_ID } from "./TaxCompletionPanel";
import { IssueReportRequestModal } from "./IssueReportRequestModal";
import { ReportRequestList } from "./ReportRequestList";
import { ReportRequestTypeHeader } from "./ReportRequestTypeHeader";
import {
  REPORT_REQUEST_TYPE_CHIP_ORDER,
  awaitingReviewRows,
  defaultSelectedType,
  openCountByType,
  overdueRequests,
  readyToReportRequests,
  requestsOfType,
} from "./reportRequestSummary";

interface ReportRequestsSectionProps {
  loan: LoanSummaryDto;
}

/** Empty-state copy per type: what "nothing here" means for THAT type, not one
 *  sentence stretched over four different situations. */
const EMPTY_COPY: Record<ReportRequestType, { title: string; description: string }> = {
  Quarterly: {
    title: "No quarterly request issued",
    description:
      "Nothing is owed until you issue one. The header above shows the quarter now reportable.",
  },
  Annual: {
    title: "No annual request issued",
    description:
      "An annual request is due 30 days after the borrower's taxes are completed, so the loan needs that date on file first.",
  },
  DscrWatchMonthly: {
    title: "No monthly DSCR request issued",
    description:
      "Monthly requests are issued by hand, one per month, while the loan is on DSCR-Watch.",
  },
  DefaultWorkout: {
    title: "No default / workout request issued",
    description:
      "Issue one to collect what a workout needs. It links to the loan's open default case and shows on that case's timeline.",
  },
};

/** Sends the reader to the control that clears a block, and puts the cursor in
 *  it — a door that scrolls to a panel and leaves them hunting is half a door. */
function focusControl(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  el.focus({ preventScroll: true });
}

export function ReportRequestsSection({ loan }: ReportRequestsSectionProps) {
  const auth = useAuth();
  const tz = auth.user?.timeZone;
  const overview = useLoanReportRequests(loan.id);
  // Already cached by the loan page, and the only read of default-case state a
  // B3 can make: `Defaults.Read` is deliberately withheld from B3, so calling
  // the Defaults API here would 403 for this surface's primary user. Loan
  // health needs `Loans.Read`, which everyone reading this tab already holds.
  const health = useLoanHealth(loan.id);

  // 🔴 THE PERMISSION STRING IS THE POLICY'S VALUE, NOT THE C# CONSTANT'S NAME.
  // This read `auth.hasPermission("Compliance.ReviewReportRequests")` — the
  // fully-qualified name of `Permissions.Compliance.ReviewReportRequests`,
  // whose VALUE is "Reporting.ReviewRequests". Claims carry values, so the
  // string being tested was never in any user's permission set: every write on
  // this tab was withheld from B3, the role the module was built for and the
  // only non-ADMIN holder of the grant, while ADMIN sailed through on the
  // `|| isAdmin` beside it. Nothing 403d, because the calls were never made.
  const isAdmin = auth.hasRoleCode("ADMIN");
  const canReview = auth.hasPermission("Reporting.ReviewRequests") || isAdmin;
  const canToggleWatch =
    auth.hasPermission("Compliance.RecordFinancialOpsCheck") || isAdmin;

  const [issueOpen, setIssueOpen] = useState(false);
  const [issueType, setIssueType] = useState<ReportRequestType>("Quarterly");
  const [selectedType, setSelectedType] = useState<ReportRequestType | null>(null);
  const [openRequestId, setOpenRequestId] = useState<string | null>(null);

  const requests = useMemo(() => overview.data?.requests ?? [], [overview.data]);
  const counts = useMemo(() => openCountByType(requests), [requests]);

  // Land on the leftmost type with something open; Quarterly when all quiet.
  // Seeded ONCE, from the first payload — re-deriving it on every refetch would
  // move the reader's filter under them when a request they just finished stops
  // being open.
  const seeded = selectedType !== null;
  useEffect(() => {
    if (seeded || !overview.data) return;
    setSelectedType(defaultSelectedType(counts));
  }, [seeded, overview.data, counts]);

  const activeType = selectedType ?? "Quarterly";
  const typeRequests = useMemo(
    () => requestsOfType(requests, activeType),
    [requests, activeType],
  );

  const openIssue = (type: ReportRequestType) => {
    setIssueType(type);
    setIssueOpen(true);
  };

  const watch = overview.data?.dscrWatch;
  const hasOpenDefaultCase =
    (health.data?.disciplines.find((d) => d.discipline === "defaults")?.count ?? 0) > 0;

  // --- The attention strip ---------------------------------------------------
  // Only numbers that are real. Every one is either a server-computed field or
  // a count of rows carrying a server-populated flag; nothing here is a guess,
  // and there is deliberately no chip for a state that exists for one type but
  // has no equivalent for the others.
  // `tz` (resolved at the top of this component) is threaded rather than
  // defaulted: this count sits beside the two child surfaces that already
  // receive it, and a chip that counted in a different zone from the rows
  // it summarises is the same one-surface-two-rules defect one level up.
  const overdue = useMemo(() => overdueRequests(requests), [requests, tz]);
  const awaiting = useMemo(() => awaitingReviewRows(requests), [requests]);
  const ready = useMemo(() => readyToReportRequests(requests), [requests]);

  const chips: AttentionChip[] = [
    {
      id: "overdue",
      label: "Overdue",
      count: overdue.length,
      tone: "error",
      items: [],
    },
    {
      id: "awaiting-review",
      // Counted off `awaitingReview`, the server's own live-staged-submission
      // flag — NOT `status === "submitted"`, which no writer ever sets, so a
      // status-derived count would read zero on a full queue.
      label: "Waiting for your review",
      count: awaiting.length,
      tone: "warning",
      items: [],
    },
    {
      id: "ready-to-report",
      label: "Ready to report",
      count: ready.length,
      tone: "warning",
      items: [],
    },
    {
      // 🔴 Named for its type, because it EXISTS only for its type. There is no
      // "not yet issued" fact for Quarterly or Annual — the server sends
      // `currentMonthIssued` for DSCR-Watch alone, precisely because those
      // requests are issued by hand each month and a missed month is otherwise
      // indistinguishable from a month nobody has reached. One chip meaning
      // something different per type would be worse than no chip.
      id: "dscr-month",
      label: "DSCR month not issued",
      count: watch?.isOnDscrWatch && !watch.currentMonthIssued ? 1 : 0,
      tone: "warning",
      items: [],
    },
  ];

  /** A chip selects the type it belongs to and, when it names exactly one
   *  request, opens it — the click ends where the work is. */
  const onChipClick = (chipId: string) => {
    if (chipId === "dscr-month") {
      setSelectedType("DscrWatchMonthly");
      setOpenRequestId(null);
      return;
    }
    const rows =
      chipId === "overdue"
        ? overdue
        : chipId === "awaiting-review"
          ? awaiting.map((row) => row.request)
          : ready;
    // Several waiting items can belong to one request — the reader is being
    // sent to requests, so count those.
    const unique = Array.from(new Map(rows.map((r) => [r.id, r])).values());
    if (unique.length === 0) return;
    setSelectedType(unique[0].requestType);
    setOpenRequestId(unique.length === 1 ? unique[0].id : null);
  };

  return (
    <section className="flex flex-col gap-space-3" data-testid="report-requests-section">
      <div className="min-w-0">
        <h3 className="text-body font-semibold text-onyx m-0">Report requests</h3>
        <p className="text-caption text-gray-mid m-0 mt-space-1 max-w-prose">
          What you have asked this borrower for, and where each item stands. A
          request arrives in their portal inbox as one bundle; you review it item
          by item here.
        </p>
      </div>

      {overview.isLoading && (
        <div className="flex flex-col gap-space-2" data-testid="report-requests-loading">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      )}

      {overview.isError && (
        <Card>
          <p className="text-small text-status-error m-0" role="alert">
            Could not load report requests.
          </p>
          <Button
            variant="secondary"
            size="sm"
            className="mt-space-2"
            onClick={() => void overview.refetch()}
          >
            Retry
          </Button>
        </Card>
      )}

      {overview.data && watch && (
        <>
          <AttentionStrip
            chips={chips}
            testId="report-requests-attention"
            onChipClick={onChipClick}
          />

          <FilterChips
            label="Request type"
            activeId={activeType}
            onChange={(id) => {
              setSelectedType(id as ReportRequestType);
              setOpenRequestId(null);
            }}
            chips={REPORT_REQUEST_TYPE_CHIP_ORDER.map((type) => ({
              id: type,
              label: REPORT_REQUEST_TYPE_LABEL[type],
              // Always a number, never undefined: `FilterChips` renders no
              // badge at all for undefined, which is exactly the "did it load?"
              // ambiguity a measured zero exists to remove.
              count: counts[type],
            }))}
          />

          <ReportRequestTypeHeader
            loanId={loan.id}
            type={activeType}
            requests={requests}
            canReview={canReview}
            canToggleWatch={canToggleWatch}
            watch={watch}
            hasOpenDefaultCase={hasOpenDefaultCase}
            onIssue={openIssue}
            onOpenRequest={(request) => {
              setSelectedType(request.requestType);
              setOpenRequestId(request.id);
            }}
            onRecordTaxDate={() => focusControl(TAX_COMPLETION_INPUT_ID)}
            onPutOnWatch={() => focusControl(DSCR_WATCH_TOGGLE_ID)}
          />

          {/* Each type's own precondition surface sits with the type it gates,
              rather than as loan-wide furniture above all four: a reader on
              Quarterly has no use for the tax-completion date, and a reader on
              Annual needs it beside the block it causes. */}
          {activeType === "Annual" && (
            <TaxCompletionPanel
              loanId={loan.id}
              taxesCompletedAt={overview.data.taxesCompletedAt}
              recordedByUserName={overview.data.taxesCompletedRecordedByUserName}
              canEdit={canReview}
            />
          )}

          {activeType === "DscrWatchMonthly" && (
            <DscrWatchPanel
              loanId={loan.id}
              watch={watch}
              canToggle={canToggleWatch}
              tz={tz}
            />
          )}

          <ReportRequestList
            loanId={loan.id}
            requests={typeRequests}
            canReview={canReview}
            openRequestId={openRequestId}
            onOpenRequestChange={setOpenRequestId}
            emptyTitle={EMPTY_COPY[activeType].title}
            emptyDescription={EMPTY_COPY[activeType].description}
            tz={tz}
          />
        </>
      )}

      {canReview && (
        <IssueReportRequestModal
          loanId={loan.id}
          open={issueOpen}
          onClose={() => setIssueOpen(false)}
          initialType={issueType}
        />
      )}
    </section>
  );
}
